// ✅ THEME-ONLY UPDATE: AdminFeeManagement -> match StudentAttendanceByStudent theme EXACTLY
// - Same tokens: THEME_ORANGE, THEME_ORANGE_2, BG, BORDER, BORDER_HOVER, BORDER_FOCUS, ROW_ALT, ROW_HOVER
// - Same surfaces: sectionSx with left orange stripe + surfaceSx
// - Same inputSx (focused label orange) + Autocomplete chips/indicators styling (autoCompleteSx)
// - Same table head padding (py: 1) + zebra rows + hover tint
// - Same toast style (rounded + subtle blue border) + anchor bottom center
// - LinearProgress orange bar
// ✅ NO API / logic changed

import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  Autocomplete,
  TableHead,
  TableRow,
  Paper,
  Button,
  Typography,
  Snackbar,
  Alert,
  TextField,
  LinearProgress,
  CircularProgress,
} from "@mui/material";
import { API_URL, API_UPLOAD_URL } from "./config";

const AdminFeeManagement = () => {
  const [fees, setFees] = useState([]);
  const [logincampusCode, setCampusCode] = useState("");
  const [username, setUsername] = useState("");
  const [studentId, setStudentId] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const token = localStorage.getItem("auth");

  // dropdown filters and options
  const [filters, setFilters] = useState({
    SubCampusCode: [],
    DegreeCode: [],
    BranchCode: [],
    SemesterCode: [],
  });

  const [options, setOptions] = useState({
    SubCampusCode: [],
    DegreeCode: [],
    BranchCode: [],
    SemesterCode: [],
  });

  const [loadingLookups, setLoadingLookups] = useState(false);

  // students list based on dropdown filters
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [allFilteredStudents, setAllFilteredStudents] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState("");
  const [isCollapsed, setIsCollapsed] = useState(false);

  // ---------------- THEME TOKENS (match StudentAttendanceByStudent) ----------------
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";

  const pageWrapSx = { p: 2, backgroundColor: BG };

  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
    backgroundColor: "transparent",
  };

  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };

  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "#fff",
    border: `1px solid ${BORDER}`,
  };

  // ✅ section surface with left orange stripe
  const sectionSx = {
    ...surfaceSx,
    p: 2,
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
      opacity: 0.95,
    },
  };

  const sectionInnerPadSx = { pl: 1 };

  // ✅ INPUTS: outline + focus label orange (exact)
  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
    "& .MuiInputLabel-root.Mui-focused": { color: THEME_ORANGE },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
    whiteSpace: "nowrap",
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
    whiteSpace: "nowrap",
  };

  const tableWrapSx = {
    ...surfaceSx,
    overflow: "hidden",
    overflowX: "auto",     // ✅ allow left-right scroll
    overflowY: "hidden",
    WebkitOverflowScrolling: "touch", // ✅ smooth iOS scrolling
  };

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
      py: 1, // ✅ compact head like report
    },
  };

  const zebraRowSx = (idx) => ({
    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#FFFFFF",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)", py: 1 },
  });

  const chipSx = {
    borderRadius: 2,
    backgroundColor: "rgba(25,118,210,0.06)",
    border: "1px solid rgba(25,118,210,0.18)",
    fontWeight: 800,
  };

  // ✅ Autocomplete styling (exact)
  const autoCompleteSx = {
    ...inputSx,
    "& .MuiAutocomplete-tag": {
      borderRadius: 8,
      backgroundColor: "rgba(25,118,210,0.06)",
      border: "1px solid rgba(25,118,210,0.18)",
      fontWeight: 800,
    },
    "& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator": {
      color: "rgba(25,118,210,0.75)",
      "&:hover": { backgroundColor: "rgba(25,118,210,0.06)" },
    },
  };

  // Fetch CampusCode from JWT
  useEffect(() => {
    if (token) {
      try {
        const decodedToken = JSON.parse(atob(token.split(".")[1]));
        setCampusCode(decodedToken.campusCode || decodedToken.CampusCode || "");
        setUsername(decodedToken.username || decodedToken.Username || "");
      } catch (err) {
        console.error("Error decoding token:", err);
        setError("Failed to decode authentication token.");
      }
    }
  }, [token]);

  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  // fetch dropdown lookups
  useEffect(() => {
    if (!logincampusCode) return;
    let cancelled = false;

    (async () => {
      try {
        setLoadingLookups(true);
        const { data } = await axios.get(`${API_URL}/api/lookups/all`, {
          ...authHeader,
          params: { CampusCode: logincampusCode },
        });

        if (cancelled) return;
        setOptions({
          SubCampusCode: data?.SubCampusCode || [],
          DegreeCode: data?.DegreeCode || [],
          BranchCode: data?.BranchCode || [],
          SemesterCode: (data?.SemesterCode || []).map(String),
        });
      } catch (e) {
        console.error("Failed to load dropdowns:", e);
      } finally {
        if (!cancelled) setLoadingLookups(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [logincampusCode, authHeader]);

  // fetch fees (report only)
  const fetchFees = async (idParam) => {
    const sid = idParam || studentId;

    if (!sid || !logincampusCode || !username) {
      setError("Please enter a valid Student ID.");
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/students/${sid}/fees`, {
        ...authHeader,
      });
      setFees(response.data || []);
      setError("");
      setStudentId(sid);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch fees for the student.");
    }
  };

  const getSubtotal = (field) => fees.reduce((acc, fee) => acc + Number(fee[field] || 0), 0);

  const feeAmountSubtotal = getSubtotal("FeeAmount");
  const feePaidSubtotal = getSubtotal("FeePaid");
  const discountSubtotal = getSubtotal("DiscountAmount");
  const refundSubtotal = getSubtotal("RefundAmount");
  const feeDueSubtotal = getSubtotal("FeeDue");

  // filter students using dropdowns
  const anyFilterSelected =
    (filters.SubCampusCode?.length || 0) > 0 ||
    (filters.DegreeCode?.length || 0) > 0 ||
    (filters.BranchCode?.length || 0) > 0 ||
    (filters.SemesterCode?.length || 0) > 0;

  const handleFilterStudents = async () => {
    setListError("");
    setFilteredStudents([]);
    setAllFilteredStudents([]);
    setIsCollapsed(false);

    if (!logincampusCode) return;
    if (!anyFilterSelected) {
      setListError("Select at least one dropdown filter to load students.");
      return;
    }

    try {
      setLoadingList(true);
      const params = { CampusCode: logincampusCode };
      if (filters.SubCampusCode.length) {params.SubCampusCode = filters.SubCampusCode.join(",");}
      if (filters.DegreeCode.length) {params.DegreeCode = filters.DegreeCode.join(",");}
      if (filters.BranchCode.length) {params.BranchCode = filters.BranchCode.join(",");}
      if (filters.SemesterCode.length) {params.SemesterCode = filters.SemesterCode.join(",");}

      const { data } = await axios.get(`${API_URL}/api/StudentFeeReportList`, {
        ...authHeader,
        params,
      });

      const arr = Array.isArray(data) ? data : [];
      setFilteredStudents(arr);
      setAllFilteredStudents(arr);

      if (!arr.length) setListError("No students found for the selected filters.");
    } catch (e) {
      console.error(e);
      setListError("Failed to load students. Please try again.");
    } finally {
      setLoadingList(false);
    }
  };

  // collapse/expand behavior on student row click
  const handleStudentRowClick = (s) => {
    if (
      isCollapsed &&
      filteredStudents.length === 1 &&
      filteredStudents[0].StudentID === s.StudentID
    ) {
      setFilteredStudents(allFilteredStudents);
      setIsCollapsed(false);
      return;
    }

    setFilteredStudents([s]);
    setIsCollapsed(true);

    setStudentId(s.StudentID);
    fetchFees(s.StudentID);
  };

  const resetAll = () => {
    setFees([]);
    setFilteredStudents([]);
    setAllFilteredStudents([]);
    setIsCollapsed(false);
    setStudentId("");
    setFilters({ SubCampusCode: [], DegreeCode: [], BranchCode: [], SemesterCode: [] });
    setListError("");
    setError("");
  };

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        {/* ✅ Orange header bar */}
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Student Fee Report
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Filter students and view fee breakup
            </Typography>
          </Box>

          <Button
            variant="outlined"
            sx={{
              ...outlinedBtnSx,
              color: "white",
              borderColor: "rgba(255,255,255,0.7)",
              "&:hover": { borderColor: "white", backgroundColor: "rgba(255,255,255,0.12)" },
            }}
            onClick={resetAll}
          >
            Reset
          </Button>
        </Box>

        <Box sx={bodySx}>
          {error ? (
            <Alert
              severity="error"
              sx={{ mb: 2, borderRadius: 2, border: "1px solid rgba(25,118,210,0.18)" }}
            >
              {error}
            </Alert>
          ) : null}

          <Snackbar
            open={success}
            autoHideDuration={3000}
            onClose={() => setSuccess(false)}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              severity="success"
              sx={{ borderRadius: 2, border: "1px solid rgba(25,118,210,0.18)" }}
              onClose={() => setSuccess(false)}
            >
              Operation successful!
            </Alert>
          </Snackbar>

          {/* Filters Row */}
          <Paper variant="outlined" sx={{ ...sectionSx, mb: 2 }}>
            <Box sx={sectionInnerPadSx}>
              <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>Filters</Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(5, 1fr)" },
                  gap: 1,
                  textAlign: "left",
                }}
              >
                <Autocomplete
                  multiple
                  options={options.SubCampusCode || []}
                  value={filters.SubCampusCode || []}
                  onChange={(_, value) => setFilters((p) => ({ ...p, SubCampusCode: value }))}
                  disableCloseOnSelect
                  filterSelectedOptions
                  openOnFocus
                  loading={loadingLookups}
                  sx={autoCompleteSx}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="SubCampusCode(s)"
                      size="small"
                      fullWidth
                      sx={inputSx}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingLookups ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                <Autocomplete
                  multiple
                  options={options.DegreeCode || []}
                  value={filters.DegreeCode || []}
                  onChange={(_, value) => setFilters((p) => ({ ...p, DegreeCode: value }))}
                  disableCloseOnSelect
                  filterSelectedOptions
                  openOnFocus
                  loading={loadingLookups}
                  sx={autoCompleteSx}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="DegreeCode(s)"
                      size="small"
                      fullWidth
                      sx={inputSx}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingLookups ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                <Autocomplete
                  multiple
                  options={options.BranchCode || []}
                  value={filters.BranchCode || []}
                  onChange={(_, value) => setFilters((p) => ({ ...p, BranchCode: value }))}
                  disableCloseOnSelect
                  filterSelectedOptions
                  openOnFocus
                  loading={loadingLookups}
                  sx={autoCompleteSx}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="BranchCode(s)"
                      size="small"
                      fullWidth
                      sx={inputSx}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingLookups ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                <Autocomplete
                  multiple
                  options={(options.SemesterCode || []).map(String)}
                  value={(filters.SemesterCode || []).map(String)}
                  onChange={(_, value) => setFilters((p) => ({ ...p, SemesterCode: value }))}
                  disableCloseOnSelect
                  filterSelectedOptions
                  openOnFocus
                  loading={loadingLookups}
                  sx={autoCompleteSx}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="SemesterCode(s)"
                      size="small"
                      fullWidth
                      sx={inputSx}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingLookups ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                  <Button
                    variant="contained"
                    onClick={handleFilterStudents}
                    disabled={loadingLookups || loadingList}
                    sx={{ ...primaryBtnSx, height: 36, fontSize: "0.85rem", minWidth: "80px" }}
                  >
                    {loadingList ? "Loading…" : "Filter Students"}
                  </Button>

                  {listError ? (
                    <Typography variant="body2" color="error" sx={{ fontWeight: 700 }}>
                      {listError}
                    </Typography>
                  ) : null}
                </Box>
              </Box>

              {loadingList ? (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress
                    sx={{
                      height: 8,
                      borderRadius: 999,
                      backgroundColor: "rgba(25,118,210,0.10)",
                      "& .MuiLinearProgress-bar": { backgroundColor: THEME_ORANGE, borderRadius: 999 },
                    }}
                  />
                </Box>
              ) : null}
            </Box>
          </Paper>

          {/* Student Search Section */}
          <Paper variant="outlined" sx={{ ...sectionSx, mb: 2 }}>
            <Box sx={sectionInnerPadSx}>
              <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                Quick Search
              </Typography>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <TextField
                  fullWidth
                  label="Enter Student ID"
                  value={studentId}
                  onChange={(e) => setStudentId(e.target.value)}
                  sx={inputSx}
                />
                <Button
                  variant="contained"
                  onClick={() => fetchFees()}
                  sx={{ ...primaryBtnSx, height: 40, minWidth: 110 }}
                  type="button"
                >
                  Search
                </Button>
              </Box>
            </Box>
          </Paper>

          {/* Students list table */}
          {filteredStudents.length > 0 ? (
            <Paper variant="outlined" sx={{ ...sectionSx, mb: 2, p: 1.5 }}>
              <Box sx={sectionInnerPadSx}>
                <Typography sx={{ mb: 1, fontWeight: 900, color: THEME_ORANGE }}>
                  Students Matching Filters
                </Typography>

                <Box sx={tableWrapSx}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={theadRowSx}>
                          <TableCell>#</TableCell>
                          <TableCell>StudentID</TableCell>
                          <TableCell>Full Name</TableCell>
                          <TableCell>SubCampus</TableCell>
                          <TableCell>Degree</TableCell>
                          <TableCell>Branch</TableCell>
                          <TableCell>Semester</TableCell>
                          <TableCell>Total Due</TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {filteredStudents.map((s, idx) => (
                          <TableRow
                            key={`${s.StudentID}-${idx}`}
                            hover
                            onClick={() => handleStudentRowClick(s)}
                            sx={zebraRowSx(idx)}
                          >
                            <TableCell>{idx + 1}</TableCell>
                            <TableCell sx={{ fontWeight: 800, color: "rgba(25,118,210,0.95)" }}>
                              {s.StudentID}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>{s.FullName}</TableCell>
                            <TableCell>{s.SubCampusCode}</TableCell>
                            <TableCell>{s.DegreeCode}</TableCell>
                            <TableCell>{s.BranchCode}</TableCell>
                            <TableCell>{s.SemesterCode}</TableCell>
                            <TableCell sx={{ fontWeight: 900, color: THEME_ORANGE }}>
                              {s.TotalDue}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Box>
            </Paper>
          ) : null}

          {/* Fee Details Table */}
          {fees.length > 0 ? (
            <Paper variant="outlined" sx={{ ...sectionSx, p: 1.5 }}>
              <Box sx={sectionInnerPadSx}>
                <Typography sx={{ mb: 1, fontWeight: 900, color: THEME_ORANGE }}>
                  Fee Details
                </Typography>

                <Box sx={tableWrapSx}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={theadRowSx}>
                          <TableCell>#</TableCell>
                          <TableCell>Fee Type</TableCell>
                          <TableCell>Fee</TableCell>
                          <TableCell>Paid</TableCell>
                          <TableCell>Discount</TableCell>
                          <TableCell>Refund</TableCell>
                          <TableCell>Due</TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {fees.map((fee, index) => (
                          <TableRow key={fee.StudentFeeID || index} sx={zebraRowSx(index)}>
                            <TableCell>{index + 1}</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>
                              {fee.RowType === "BUNDLE" ? `${fee.Semester}-${fee.StudentLabel}` : `${fee.Semester}-${fee.FeeType}`}
                            </TableCell>
                            <TableCell>{fee.FeeAmount}</TableCell>
                            <TableCell sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)" }}>
                              {fee.FeePaid}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 900, color: THEME_ORANGE }}>
                              {fee.DiscountAmount}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 900, color: "#D11A1C" }}>
                              {fee.RefundAmount}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 900, color: "#D32F2F" }}>
                              {fee.FeeDue}
                            </TableCell>
                            
                          </TableRow>
                        ))}

                        {/* Subtotal Row */}
                        <TableRow sx={{ backgroundColor: "rgba(25,118,210,0.08)" }}>
                          <TableCell colSpan={2} sx={{ fontWeight: 900 }}>
                            Subtotal
                          </TableCell>
                          <TableCell sx={{ fontWeight: 900 }}>₹{feeAmountSubtotal}</TableCell>
                          <TableCell sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)" }}>
                            ₹{feePaidSubtotal}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 900, color: THEME_ORANGE }}>
                            ₹{discountSubtotal}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 900, color: "#D11A1C" }}>
                            ₹{refundSubtotal}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 900, color: "#D32F2F" }}>
                            ₹{feeDueSubtotal}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Box>
            </Paper>
          ) : null}
        </Box>
      </Paper>
    </Box>
  );
};

export default AdminFeeManagement;
